import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Play, ExternalLink, Download, Loader2 } from 'lucide-react';
import ImageUploader from '../../components/admin/ImageUploader';
import { getImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fetchingIg, setFetchingIg] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    type: 'aftermovie',
    category: 'openair',
    thumbnail: '',
    youtube_url: '',
    instagram_url: '',
    format: 'horizontal'
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/videos`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const handleSubmit = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      if (editingVideo) {
        await axios.put(`${BACKEND_URL}/api/admin/videos/${editingVideo.id}`, formData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Vidéo mise à jour!');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/videos`, formData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Vidéo ajoutée!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchVideos();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/videos/${videoId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Vidéo supprimée!');
      fetchVideos();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (video) => {
    setEditingVideo(video);
    setFormData({
      ...video,
      format: video.format || 'horizontal' // fallback si format absent
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingVideo(null);
    setFormData({
      id: '',
      title: '',
      type: 'aftermovie',
      category: 'openair',
      thumbnail: '',
      youtube_url: '',
      instagram_url: '',
      format: 'horizontal'
    });
  };

  // Extract YouTube thumbnail from URL
  const getYoutubeThumbnail = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
    return '';
  };

  const fetchInstagramPreview = async () => {
    const url = (formData.instagram_url || '').trim();
    if (!url) {
      toast.error('Colle d\'abord une URL Instagram');
      return;
    }
    setFetchingIg(true);
    const auth = localStorage.getItem('admin_auth');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/fetch-instagram-preview`,
        { url },
        { headers: { Authorization: `Basic ${auth}` } }
      );
      setFormData((prev) => ({ ...prev, thumbnail: res.data.url }));
      toast.success('Preview Instagram récupérée !');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de la récupération';
      toast.error(msg);
    } finally {
      setFetchingIg(false);
    }
  };

  return (
    <div data-testid="admin-videos-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Vidéos</h1>
          <p className="text-sm text-[#B9B2A8]">Gérez vos aftermovies et teasers</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0C] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-[#F5F0E8] font-display">
                {editingVideo ? 'Modifier la vidéo' : 'Nouvelle vidéo'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Open Air 2024 - Aftermovie"
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Type</label>
                  <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B0B0C] border-white/10">
                      <SelectItem value="aftermovie" className="text-[#F5F0E8]">Aftermovie</SelectItem>
                      <SelectItem value="teaser" className="text-[#F5F0E8]">Teaser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Catégorie</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B0B0C] border-white/10">
                      <SelectItem value="openair" className="text-[#F5F0E8]">Open Air</SelectItem>
                      <SelectItem value="mirano" className="text-[#F5F0E8]">Mirano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Format</label>
                <Select value={formData.format} onValueChange={(v) => setFormData({ ...formData, format: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0B0C] border-white/10">
                    <SelectItem value="horizontal" className="text-[#F5F0E8]">Horizontal (16:9)</SelectItem>
                    <SelectItem value="vertical" className="text-[#F5F0E8]">Vertical (9:16)</SelectItem>
                    <SelectItem value="story" className="text-[#F5F0E8]">Story (1:1)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">URL YouTube</label>
                <Input
                  value={formData.youtube_url}
                  onChange={(e) => {
                    const url = e.target.value;
                    setFormData({ 
                      ...formData, 
                      youtube_url: url,
                      thumbnail: getYoutubeThumbnail(url) || formData.thumbnail
                    });
                  }}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">URL Instagram (optionnel)</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.instagram_url || ''}
                    onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                    placeholder="https://www.instagram.com/p/... ou /reel/..."
                    className="flex-1 bg-white/5 border-white/10 text-[#F5F0E8]"
                  />
                  <Button
                    type="button"
                    onClick={fetchInstagramPreview}
                    disabled={fetchingIg || !formData.instagram_url}
                    className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] whitespace-nowrap"
                    title="Récupérer automatiquement l'image de couverture depuis Instagram"
                  >
                    {fetchingIg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Auto-preview
                  </Button>
                </div>
                <p className="text-xs text-[#B9B2A8] mt-1">
                  Clique "Auto-preview" pour remplir la miniature automatiquement depuis Instagram
                </p>
              </div>
              <ImageUploader
                label="Miniature"
                value={formData.thumbnail}
                onChange={(url) => setFormData({ ...formData, thumbnail: url })}
              />
              <p className="text-xs text-[#B9B2A8] -mt-2">
                Remplie automatiquement par "Auto-preview", ou uploade ton propre screenshot. C'est ce que verra le public.
              </p>
              <Button onClick={handleSubmit} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                {editingVideo ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {videos.map((video, index) => (
          <motion.div
            key={video.id || video._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white/5 border border-white/10 rounded-xl overflow-hidden group"
          >
            <div className="relative aspect-video">
              <img src={getImageUrl(video.thumbnail)} alt={video.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <a href={video.instagram_url || video.youtube_url} target="_blank" rel="noopener noreferrer">
                  <div className="w-12 h-12 rounded-full bg-[#C9A84C] flex items-center justify-center">
                    <Play className="w-5 h-5 text-[#060606] ml-1" />
                  </div>
                </a>
              </div>
              <div className="absolute top-2 right-2 flex gap-1">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${
                  video.type === 'aftermovie' ? 'bg-purple-500/80 text-white' : 'bg-blue-500/80 text-white'
                }`}>
                  {video.type}
                </span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-sm text-[#F5F0E8] mb-2 line-clamp-1">{video.title}</h3>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#B9B2A8]">
                  {video.category === 'openair' ? 'Open Air' : 'Mirano'}
                </span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(video)} className="h-7 w-7 text-[#B9B2A8] hover:text-[#F5F0E8]">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(video.id)} className="h-7 w-7 text-[#B9B2A8] hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AdminVideosPage;
